import { Label } from '@kcftech/design-system';
import { FilesUpdateRequest } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { DefaultButton } from 'components/button/DefaultButton';
import { Detail } from 'components/detail/detail';
import { DetailsColumns, DetailsSection } from 'components/detail/styled';
import { DocumentTitle } from 'components/DocumentTitle';
import { MultiSelectOption } from 'components/forms/multiselect/MultiSelect';
import { MultiSelectTagList } from 'components/forms/multiselect/MultiSelectTagList';
import { AuthorizedImage } from 'components/image/AuthorizedImage';
import { DetailsPage } from 'components/page/DetailsPage';
import { InfoPopup } from 'components/popup/InfoPopup';
import { Spinner } from 'components/spinner/Spinner';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useTreeItems } from 'custom_hooks/data_hooks/tree/useTreeItems';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { AssetMultiSelect } from 'pages/Settings/FileLibrary/FileDetails/AssetMultiSelect';
import { FileDetailsDescriptionEditor } from 'pages/Settings/FileLibrary/FileDetails/FileDetailsDescriptionEditor';
import { FileDetailsNameEditor } from 'pages/Settings/FileLibrary/FileDetails/FileDetailsNameEditor';
import { getFileUrl } from 'pages/Settings/FileLibrary/FileLibraryTable';
import { useFileFromLibrary } from 'pages/Settings/FileLibrary/useFileFromLibrary';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { borderColor } from 'styles/theme';
import { arraysAreSame } from 'utils/array';
import { buildTooltip } from 'utils/group_hierarchy';

export const FileDetailsColumns = styled(DetailsColumns)`
    margin-top: 2rem;
    margin-left: 48px;

    @media ${MediaQueries.xlargeAndUp} {
        grid-template-columns: minmax(min-content, auto) minmax(30rem, 1fr) minmax(
                20rem,
                1fr
            );
        grid-template-areas: 'details-section';
        grid-gap: 10%;
    }
`;

export const AssetSelectionArea = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 1.5rem;
    margin-bottom: 1rem;
    max-width: 40%;
`;

export const FileDetailsSection = styled.div`
    display: flex;
    flex-direction: column;
    border-bottom: solid 1px ${borderColor};
    padding: 3rem;
`;

export const SelectedAssetsSection = styled.div`
    padding: 3rem;
`;

export const FileName = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
`;

const ImageArea = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    overflow-y: auto;
`;

const ImageContainer = styled.div`
    width: fit-content;
    padding: 3rem;
`;

const FullSizedImage = styled(AuthorizedImage)`
    height: auto;
    width: auto;
    max-height: 45rem;
    max-width: 100%;
`;

type FileDetailsPageProps = Readonly<{
    refreshTable: () => void;
}>;

export function FileDetailsPage({ refreshTable }: FileDetailsPageProps) {
    const { showToast } = useNotificationContext();
    const { fileId } = useParams<{ fileId: string }>();
    const { canEditFileDetails } = usePermissions();

    const { data: file, isLoading: isFileLoading, refetch } = useFileFromLibrary(fileId);

    const { formatDateTime } = useUserDateTimeFormat();

    const [newSelectedAssets, setNewSelectedAssets] = useState<
        MultiSelectOption<string>[]
    >([]);
    const [initialSelectedAssets, setInitialSelectedAssets] = useState<
        MultiSelectOption<string>[]
    >([]);
    const { corporation } = useRouteEntities();
    const associatedAssetIds = useMemo(() => file?.GroupIds ?? [], [file]);
    const disableSaveButton = useMemo(
        () =>
            arraysAreSame(
                initialSelectedAssets.map((ia) => ia.value),
                newSelectedAssets.map((na) => na.value)
            ),
        [initialSelectedAssets, newSelectedAssets]
    );
    // We are using the TreeItems hook temporarily until we refactor the files api to
    // return an array of GroupChildren instead of GroupIds so that we have the asset names
    const { data: fileAssets, isLoading: isAssetsLoading } = useTreeItems(
        associatedAssetIds
    );

    const { mutate: saveFileAssets } = useMutation({
        mutationFn: () => {
            if (!file) {
                throw new Error('Missing file');
            }
            return sdClient.updateFile({
                fileId: file.Id,
                body: new FilesUpdateRequest({
                    DisplayName: file.DisplayName,
                    ParentFolderId: file.ParentFolderId ?? '',
                    Description: file.Description,
                    GroupIds: newSelectedAssets.map((sa) => sa.value),
                }),
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully updated asset assignments.',
            });
            setInitialSelectedAssets(newSelectedAssets);
        },

        onError: (_e) => {
            showToast({
                type: 'error',
                content: 'An error occurred when updating asset assignments.',
            });
        },
    });

    useEffect(() => {
        setNewSelectedAssets(
            fileAssets?.map((fa) => ({
                title: buildTooltip(fa.Hierarchy, undefined, fa.Name),
                value: fa.Id,
            })) ?? []
        );
        setInitialSelectedAssets(
            fileAssets?.map((fa) => ({
                title: buildTooltip(fa.Hierarchy, undefined, fa.Name),
                value: fa.Id,
            })) ?? []
        );
    }, [fileAssets]);

    const isLoading = isFileLoading || isAssetsLoading;
    const hasError = !file && !isLoading;

    return (
        <DetailsPage
            isLoading={isLoading}
            error={hasError ? 'No file data' : undefined}
            invalidURL={hasError}
            fullHeight
            fullWidth
        >
            <DocumentTitle title={`${file?.DisplayName ?? ''} File Details`} />
            {file && (
                <>
                    <FileDetailsSection>
                        <FileName>
                            <FileDetailsNameEditor
                                canEdit={canEditFileDetails}
                                file={file}
                                refetchFile={refetch}
                                refreshTable={refreshTable}
                            />
                        </FileName>
                        <FileDetailsColumns>
                            <DetailsSection>
                                <Detail name="Description">
                                    <FileDetailsDescriptionEditor
                                        canEdit={canEditFileDetails}
                                        file={file}
                                        refetchFile={refetch}
                                        refreshTable={refreshTable}
                                    />
                                </Detail>
                                <Detail name="Resolution">
                                    {file.ImageWidth}
                                    <span className="mx-5">x</span>
                                    {file.ImageHeight}
                                </Detail>
                            </DetailsSection>
                            <DetailsSection>
                                <Detail name="Uploaded">
                                    {formatDateTime(file.CreationDate)}
                                </Detail>
                                <Detail name="Last Update">
                                    {formatDateTime(file.UpdateDate)}
                                </Detail>
                            </DetailsSection>
                        </FileDetailsColumns>
                    </FileDetailsSection>
                    <SelectedAssetsSection>
                        {file.Location ? (
                            <DetailsSection>
                                {canEditFileDetails ? (
                                    <>
                                        <AssetSelectionArea>
                                            <AssetMultiSelect
                                                corporationId={corporation?.Id ?? ''}
                                                locationId={file.Location.LocationId}
                                                selectedAssets={newSelectedAssets}
                                                onChange={setNewSelectedAssets}
                                            />
                                            <DefaultButton
                                                title="Save Assets to File"
                                                disabled={disableSaveButton}
                                                onClick={() => saveFileAssets()}
                                            >
                                                Save Assets
                                            </DefaultButton>
                                        </AssetSelectionArea>
                                        <MultiSelectTagList
                                            selectedOptions={newSelectedAssets}
                                            onChange={setNewSelectedAssets}
                                        />
                                    </>
                                ) : (
                                    <Detail name="Assets">
                                        {newSelectedAssets.map((a) => a.title).join(', ')}
                                    </Detail>
                                )}
                            </DetailsSection>
                        ) : (
                            <div className="flex-row">
                                <Label label="Assets" className="mt-1" />
                                <InfoPopup title="Assets">
                                    <p>Only location images can be linked to assets.</p>
                                </InfoPopup>
                            </div>
                        )}
                    </SelectedAssetsSection>
                    <ImageArea>
                        <ImageContainer>
                            <FullSizedImage
                                src={getFileUrl(file)}
                                pending={<Spinner className="pr-15" />}
                            />
                        </ImageContainer>
                    </ImageArea>
                </>
            )}
        </DetailsPage>
    );
}
